import { IObstetricaLabResultOption, IObstetricaLabResultsGroup, LabObservationObstetricalFile, LabObservationResultType } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { FormSideWidgetContainer, FormWidgetPosition } from "@/Patient/Components/MedicalNote/Forms/FormSideWidgetContainer";
import { ObstetricalFileLabObservationsContainer } from "../../../../Common/Subcomponent/ObstetricalFileLabObservationsContainer";
import { OntarioObstetricalFilePageTwoState } from "../OntarioObstetricalFileOpr2";

class OntarioObstetricalFileLabWidgetProps {
    public obstetricalFileLabObservations: IObstetricaLabResultsGroup[];
    public finalEDB: Date;
    public isInEditMode: boolean;
    public mutateForm: (mutation: (state: OntarioObstetricalFilePageTwoState) => void, then?: () => void) => void;
}

class OntarioObstetricalFileLabWidget extends BaseReactComponent<OntarioObstetricalFileLabWidgetProps> {
    constructor(props: OntarioObstetricalFileLabWidgetProps) {
        super(props);
        this.import = this.import.bind(this);
    }

    public render(): JSX.Element {
        return (
            <FormSideWidgetContainer
                width="200px"
                position={FormWidgetPosition.Left}
                leftOffset="20px"
                topOffset="290px"
                containerClassName="min-height-lab-widget"
            >
                <ObstetricalFileLabObservationsContainer
                    id="OntarioLabObservations"
                    observationGroups={this.props.obstetricalFileLabObservations}
                    finalEDB={this.props.finalEDB}
                    import={this.import}
                />
            </FormSideWidgetContainer>
        );
    }

    public import(value: LabObservationObstetricalFile, options: IObstetricaLabResultOption, trimester?: number): void {
        if (!this.props.isInEditMode)
            return;

        let date = `${LuxonDateTimeParser.parse(value.CollectionDate).toFormat("[MMM dd]")}`;

        let valueWithDate = value.ValueText?.isNumber()
            ? `${value.ValueText}  ${date}`
            : `${value.ValueText} ${date}`;

        let is2ndor3rdTrimester = trimester == 2 || trimester == 3;

        switch (options?.labObservationResultType) {
            // Section depands on Trimester
            case LabObservationResultType.Hemoglobin:
                if (is2ndor3rdTrimester)
                    return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.Hemoglobin = valueWithDate);
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.Hemoglobin = valueWithDate);

            case LabObservationResultType.Platelet:
                if (is2ndor3rdTrimester)
                    return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.Platelets = valueWithDate);
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.Platelets = valueWithDate);

            case LabObservationResultType.BldGpAntibody:
                valueWithDate = valueWithDate.replace(/Not detected/gi, "Negative").replace(/detected/gi, "Positive");

                if (is2ndor3rdTrimester)
                    return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.RepeatAntibodies = valueWithDate);
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.AntibodyScreen = valueWithDate);

            case LabObservationResultType.AboRhType:
                let splitted = value.ValueText.split("RH").map(x => x.trim());
                if (splitted.length < 2)
                    break;

                let splittedRhType = splitted[1].replace(/POSITIVE/gi, "+").replace(/NEGATIVE/gi, "-");

                if (is2ndor3rdTrimester) {
                    return this.props.mutateForm(s => {
                        s.form.Opr2.SecondAndThirdTrimesterLab.BloodGroup = splitted[0];
                        s.form.Opr2.SecondAndThirdTrimesterLab.BloodRhesus = splittedRhType;
                    });
                }

                return this.props.mutateForm(s => {
                    s.form.Opr2.InitialLabInvestigations.BloodGroup = splitted[0];
                    s.form.Opr2.InitialLabInvestigations.BloodRhesus = splittedRhType;
                });

            case LabObservationResultType.AboType:
                if (is2ndor3rdTrimester)
                    return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.BloodGroup = value.ValueText);

                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.BloodGroup = value.ValueText);

            case LabObservationResultType.RhType:
                let rhType = value.ValueText.replace(/POSITIVE/gi, "+").replace(/NEGATIVE/gi, "-");
                if (is2ndor3rdTrimester)
                    return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.BloodRhesus = rhType);

                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.BloodRhesus = rhType);

            // Direct import
            case LabObservationResultType.HBVsurfAg:
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.HepatitisBSurfaceAntigenHBsAg = valueWithDate);

            case LabObservationResultType.Syphilis:
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.Syphilis = valueWithDate);
            case LabObservationResultType.McvRbc:
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.MeanCorpuscularVolume = valueWithDate);
            case LabObservationResultType.HIV12Ab:
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.HumanImmunodeficiencyVirus = valueWithDate);
            case LabObservationResultType.Chlamydia:
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.Chlamydia = valueWithDate);
            case LabObservationResultType.Gonorrhoeae:
                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.GeneralConditionGonorrheas = valueWithDate);
            case LabObservationResultType.RubellaIgGAb:
                if (/^Reactive$/.test(value.ValueText))
                    return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.RubellaImmune = `Immune ${date}`);
                if (/^Indeterminate$/.test(value.ValueText))
                    return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.RubellaImmune = `Indeterminate ${date}`);
                if (/^Non Reactive/.test(value.ValueText))
                    return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.RubellaImmune = `Not Immune ${date}`);
                if (/^\d{1,2}(\.\d{0,2})?$/.test(value.ValueText)) {
                    if (Number(value.ValueText) >= 5.0)
                        return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.RubellaImmune = `Immune ${date}`);

                    if (Number(value.ValueText) < 5.0)
                        return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.RubellaImmune = `Not Immune ${date}`);
                }

                return this.props.mutateForm(s => s.form.Opr2.InitialLabInvestigations.RubellaImmune = valueWithDate);
            case LabObservationResultType.Glucose1h50g:
            case LabObservationResultType.Glucose1h75g:
                return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.FirstHourGlucoseChallengeTest = valueWithDate);
            case LabObservationResultType.Glucose2h75g:
                return this.props.mutateForm(s => s.form.Opr2.SecondAndThirdTrimesterLab.SecondHourGlucoseToleranceTest = valueWithDate);

            default:
                return;
        }
    }
}

export { OntarioObstetricalFileLabWidget };
